import {
  PagedResponse,
  PatchWithMeta,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { MODULE_NAME } from "../moduleName";
import { foiService, sarsService, shareService } from "../services";
import {
  ActionPayload,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedItem,
  loadedItems,
  loadingItem,
  LOADING_ITEM,
  LOADING_ITEMS,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  ATTACHING_EMAIL,
  ASSIGNING_CASE,
  UNASSIGNING_CASE,
  REOPEN_SAR,
  REJECT_SAR,
  UPDATING_SHARE,
  loadedShare,
  PAUSE_SAR,
  UNPAUSE_SAR,
  LOADING_FOI,
  loadedFoi,
  CREATING_FOI,
  LOADED_FOI,
} from "./actions";
import { authSelector } from "./selectors";
import { History } from "history";
import { SarsModel } from "../domain/SarsModel";
import { Attachment } from "../domain/Attachment";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { ShareRecord } from "../domain/ShareRecord";
import { ConflictResponse } from "../domain/ConflictResponse";
function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}
function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchAttachEmailSaga() {
  yield takeLatest(ATTACHING_EMAIL, attachEmailSaga);
}

function* watchAssignCaseHandlerSaga() {
  yield takeEvery(ASSIGNING_CASE, assignCaseHandlerSaga);
}
function* watchUnassignCaseHandlerSaga() {
  yield takeEvery(UNASSIGNING_CASE, unassignCaseHandlerSaga);
}

function* watchReOpenSarSaga() {
  yield takeLatest(REOPEN_SAR, reOpenSarSaga);
}

function* watchRejectSarSaga() {
  yield takeLatest(REJECT_SAR, rejectSarSaga);
}

function* watchUpdateShareSaga() {
  yield takeLatest(UPDATING_SHARE, updateShareSaga);
}

function* watchPauseSarSaga() {
  yield takeLatest(PAUSE_SAR, pauseSarSaga);
}

function* watchUnpauseSarSaga() {
  yield takeLatest(UNPAUSE_SAR, unpauseSarSaga);
}

function* watchLoadFoiTransferRecord() {
  yield takeLatest(LOADING_FOI, loadFoiTransferRecordSaga);
}

function* watchCreateFoiSaga() {
  yield takeEvery(CREATING_FOI, createFoiSaga);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<SarsModel> = yield call(
    [sarsService, sarsService.search],
    { ...payload },
    { token: auth.token }
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const opts = { token: auth.token };
  const { item, share } = yield all({
    item: call([sarsService, sarsService.get], payload, opts),
    share: call([shareService, shareService.get], payload, opts),
  });
  yield put(loadedItem(item, share));
}
function* updateItemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, patchWithMeta, newFiles, attachmentsToDelete } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<SarsModel>;
    newFiles: Attachment[];
    attachmentsToDelete: Attachment[];
  };

  function* deleteAttachmentsCalls() {
    yield call(() =>
      attachmentsToDelete.map((doc) =>
        sarsService.deleteAttachment(id, doc.blobId, {
          token: auth.token,
        })
      )
    );
  }

  const attachmentsCall = () => {
    sarsService.postAttachments(id, newFiles, {
      token: auth.token,
    });
  };

  const { validatedResponse } = yield all({
    validatedResponse: call(
      [sarsService, sarsService.update],
      id,
      patchWithMeta,
      {
        token: auth.token,
      }
    ),
    deletedDocs: call(deleteAttachmentsCalls),
    newDocs: call(attachmentsCall),
  });

  if (validatedResponse.isValid) {
    yield put(loadingItem(id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createItemSaga({ payload }: ActionPayload<SarsModel>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const validatedResponse: ValidatedResponse<SarsModel> = yield call(
    [sarsService, sarsService.create],
    payload,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    var sar: SarsModel = validatedResponse.data;

    yield put({ type: CREATED_ITEM });
    yield call(() => navigate(`/${MODULE_NAME}/${sar.id}`));
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* attachEmailSaga({
  payload,
}: ActionPayload<{ id: string; file: File }>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, file } = payload;

  yield call([sarsService, sarsService.attachEmail], id, file, {
    token: auth.token,
  });

  yield put(refreshActivityFeed(id));
}

function* assignCaseHandlerSaga({
  payload,
}: ActionPayload<{
  id: string;
  assignedToUpn: string;
  assignedToDisplayName: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, assignedToUpn, assignedToDisplayName } = payload;

  const sar: SarsModel = yield call(
    [sarsService, sarsService.assign],
    id,
    assignedToUpn,
    assignedToDisplayName,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(sar));
}
function* unassignCaseHandlerSaga({
  payload,
}: ActionPayload<{
  id: string;
}>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const sar: SarsModel = yield call([sarsService, sarsService.unassign], id, {
    token,
  });
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(sar));
}

function* reOpenSarSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const sar: SarsModel = yield call([sarsService, sarsService.reOpenSar], id, {
    token,
  });
  yield put(loadedItem(sar));
}

function* rejectSarSaga({
  payload,
}: ActionPayload<{ id: string; response: string }>) {
  const { token } = yield select(authSelector);
  const { id, response } = payload;

  const sar: SarsModel = yield call(
    [sarsService, sarsService.reject],
    id,
    response,
    { token }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(sar));
}

function* updateShareSaga({
  payload,
}: ActionPayload<{ id: string; share: ShareRecord }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, share } = payload;

  const response: ShareRecord = yield call(
    [shareService, shareService.update],
    id,
    share,
    { token }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedShare(response));
}

function* pauseSarSaga({
  payload,
}: ActionPayload<{ id: string; reason: number; response: string }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, reason, response } = payload;

  const sar: SarsModel = yield call(
    [sarsService, sarsService.pause],
    id,
    reason,
    response,
    { token }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(sar));
}

function* unpauseSarSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id } = payload;

  const sar: SarsModel = yield call([sarsService, sarsService.unpause], id, {
    token,
  });
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(sar));
}

function* loadFoiTransferRecordSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const response: Response = yield call(
    [foiService, foiService.getPeFromFoiId],
    payload,
    {
      token: auth.token,
    }
  );

  yield put(loadedFoi(response));
}

function* createFoiSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const validatedResponse: ConflictResponse = yield call(
    [foiService, foiService.createFoi],
    payload,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.status === 204) {
    yield call(() => navigate(`/foi/${validatedResponse.data}`));
    yield put({
      type: LOADED_FOI,
    });
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchAttachEmailSaga(),
    watchAssignCaseHandlerSaga(),
    watchUnassignCaseHandlerSaga(),
    watchReOpenSarSaga(),
    watchRejectSarSaga(),
    watchUpdateShareSaga(),
    watchPauseSarSaga(),
    watchUnpauseSarSaga(),
    watchLoadFoiTransferRecord(),
    watchCreateFoiSaga(),
  ]);
}
